import { OPEN_WORLD_TICK_MS } from '@kenocyber/game-domain/open-world/client';
import { isPvpMovementKey, pvpAimVector, pvpMovementAxes } from '../pvp/controls';

/**
 * **O toque mais curto que a sala ainda vê** (gate V28I.BX.2).
 *
 * A sala guarda a intenção como um trinco e o tique lê-a de cinquenta em
 * cinquenta milissegundos. Uma tecla premida e largada entre dois tiques
 * chegava lá como duas mensagens seguidas, a segunda apagava a primeira, e o
 * toque perdia-se: nem um passo, nem um tiro. Um toque mais curto do que isto
 * fica seguro até cumprir um tique e uma folga, e só depois se larga.
 *
 * Um gesto mais comprido do que isto não é tocado: largar sai na hora.
 */
export const OPEN_WORLD_MIN_PULSE_MS = OPEN_WORLD_TICK_MS + 10;

/**
 * **O que o jogador está a segurar, no mundo aberto.**
 *
 * ## Porque não é a fonte de intenção do duelo
 *
 * Porque a do duelo carrega o que um duelo tem: o trinco do teleporte, as três
 * cartas, a soltura e a detonação. Nenhuma dessas coisas existe aqui — as cartas
 * não estão ligadas ao runtime do mundo aberto e o teleporte é de outro modo —
 * e herdar trincos que ninguém arma seria herdar código que ninguém consegue
 * apagar depois.
 *
 * O que se reutiliza é o que é genuinamente o mesmo e já é puro: **as teclas de
 * movimento** e **a projeção da mira**. As duas vêm de `pvp/controls.ts`, e é
 * por isso que WASD e o rato se comportam aqui exatamente como se comportam num
 * duelo.
 *
 * ## Os instantes
 *
 * `press`, `release` e `setFiring` recebem o instante do gesto, e `current` o
 * de agora, todos do mesmo relógio. Sem instante não há toque mínimo: largar
 * vale na hora, como antes.
 */
export class OpenWorldIntentSource {
  private readonly held = new Set<string>();
  /** Quando cada tecla segurada foi premida. */
  private readonly pressedAt = new Map<string, number>();
  /** As teclas largadas antes do toque mínimo, e quando podem sair. */
  private readonly releaseDue = new Map<string, number>();
  private firing = false;
  private firingSince = Number.NaN;
  private firingOffDue: number | null = null;
  private aim: { ax: number; ay: number } = { ax: 1, ay: 0 };

  press(code: string, at = Number.NaN): void {
    if (isPvpMovementKey(code)) {
      this.held.add(code);
      this.pressedAt.set(code, at);
      this.releaseDue.delete(code);
    }
  }

  release(code: string, at = Number.NaN): void {
    if (!this.held.has(code)) {
      return;
    }
    const since = this.pressedAt.get(code) ?? Number.NaN;
    if (at - since < OPEN_WORLD_MIN_PULSE_MS) {
      this.releaseDue.set(code, since + OPEN_WORLD_MIN_PULSE_MS);
      return;
    }
    this.held.delete(code);
    this.pressedAt.delete(code);
    this.releaseDue.delete(code);
  }

  setFiring(firing: boolean, at = Number.NaN): void {
    if (firing) {
      if (!this.firing) {
        this.firingSince = at;
      }
      this.firing = true;
      this.firingOffDue = null;
      return;
    }
    if (!this.firing) {
      return;
    }
    if (at - this.firingSince < OPEN_WORLD_MIN_PULSE_MS) {
      this.firingOffDue = this.firingSince + OPEN_WORLD_MIN_PULSE_MS;
      return;
    }
    this.firing = false;
    this.firingOffDue = null;
  }

  /**
   * Aponta para um ponto do mundo, a partir de onde a nave está.
   *
   * Pela mesma projeção que o duelo usa: um quadrante tem milhões de
   * milli-unidades de lado, e uma diferença crua transbordaria o limite que a
   * sala põe numa componente de mira. Dividir pela maior componente preserva o
   * ângulo e não pode transbordar.
   */
  aimAt(shipX: number, shipY: number, worldX: number, worldY: number): void {
    const vector = pvpAimVector(shipX, shipY, worldX, worldY);
    if (vector !== null) {
      this.aim = vector;
    }
  }

  /**
   * Larga tudo, na hora. Uma janela que perde o foco não continua a segurar
   * teclas — nem as de um toque ainda por cumprir.
   */
  clear(): void {
    this.held.clear();
    this.pressedAt.clear();
    this.releaseDue.clear();
    this.firing = false;
    this.firingOffDue = null;
  }

  /**
   * O que está a ser segurado agora. Uma fotografia, sem estado escondido: um
   * toque que já cumpriu o mínimo sai aqui, e sem `now` sai sempre.
   */
  current(now = Number.POSITIVE_INFINITY): {
    readonly mx: -1 | 0 | 1;
    readonly my: -1 | 0 | 1;
    readonly aimX: number;
    readonly aimY: number;
    readonly firing: boolean;
  } {
    for (const [code, due] of this.releaseDue) {
      if (due <= now) {
        this.held.delete(code);
        this.pressedAt.delete(code);
        this.releaseDue.delete(code);
      }
    }
    if (this.firingOffDue !== null && this.firingOffDue <= now) {
      this.firing = false;
      this.firingOffDue = null;
    }
    const axes = pvpMovementAxes(this.held);
    return {
      mx: axes.mx,
      my: axes.my,
      aimX: this.aim.ax,
      aimY: this.aim.ay,
      firing: this.firing,
    };
  }
}
